import React, { useContext, useEffect, useRef, useState } from "react";
import { Row, Col } from "react-bootstrap";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import NoteDetailPage from "../../../subsreens/gcstudent/notesdetail/NoteDetailPage";
import "../../../assets/css/gcstudent/notes/NotesPage.css";
import "../../../assets/css/gcstudent/featuredbooksection/FeaturedBooksSection.css";

const LATEST_COUNT = 4;

/* ── Per-subject accent (same map as NotesPage) ── */
const SUBJECT_ACCENTS = {
  "Computer Science":        "#4F6EF7",
  "Artificial Intelligence": "#10B981",
  "Networking":               "#0EA5E9",
  "Software Engineering":     "#F97316",
  "Electronics":              "#F59E0B",
  "Mathematics":               "#06B6D4",
  "Physics":                   "#EC4899",
  "Other":                     "#64748B",
};
const getAccent = (subject) => SUBJECT_ACCENTS[subject] || SUBJECT_ACCENTS.Other;

const getInitials = (name) => {
  if (!name || name === "Unknown") return "?";
  const parts = name.trim().split(/\s+/);
  const initials = parts.length > 1
    ? parts[0][0] + parts[1][0]
    : parts[0].slice(0, 2);
  return initials.toUpperCase();
};

/* ─────────────────────────────────────────────
   Note Card — same markup/behaviour as NoteCard
   inside NotesPage.jsx, kept here so this section
   is fully standalone.
───────────────────────────────────────────── */
const NoteCard = ({ note, index, onClick }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.06 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  };

  const authorName = note.lbteacher?.name || "Unknown";
  const subject = note.subject || "General";
  const accent  = getAccent(subject);

  return (
    <div
      ref={ref}
      className={`gcStudent-note-card ${visible ? "gcStudent-note-visible" : ""}`}
      style={{
        "--note-accent": accent,
        transitionDelay: `${(index % 8) * 0.06}s`,
      }}
      onClick={() => onClick(note)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick(note)}
    >
      <div className="gcStudent-note-topbar" />

      <div className="gcStudent-note-body">
        <span className="gcStudent-note-label">Subject</span>
        <span className="gcStudent-note-tag">{subject}</span>

        <h5 className="gcStudent-note-title">{note.title}</h5>
      </div>

      <div className="gcStudent-note-divider" />

      <div className="gcStudent-note-footer">
        <div className="gcStudent-note-author-block">
          <div className="gcStudent-note-avatar">{getInitials(authorName)}</div>
          <div className="gcStudent-note-author-meta">
            <span className="gcStudent-note-author-name">{authorName}</span>
            <span className="gcStudent-note-date">
              {formatDate(note.created_at)}
            </span>
          </div>
        </div>
      </div>

      <button
        type="button"
        className="gcStudent-note-view-btn"
        onClick={(e) => { e.stopPropagation(); onClick(note); }}
      >
        View Full Notes
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path d="M5 12h14M13 6l6 6-6 6"
            stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Featured Notes Section — mirrors
   FeaturedBooksSection / FeaturedPDFsSection
───────────────────────────────────────────── */
const FeaturedNotesSection = ({ onViewAll }) => {
  const [latestNotes,  setLatestNotes]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [selectedNote, setSelectedNote] = useState(null);

  const { baseUrl } = useContext(AppContext);

  useEffect(() => {
    let mounted = true;

    axios
      .get(`${baseUrl}/general/notes/getAllPublicNotes`)
      .then((res) => {
        if (!mounted) return;
        if (res.data.success === true) {
          const notes = (res.data.notes || []).filter(Boolean);

          const sorted = [...notes].sort((a, b) => {
            if (a.created_at && b.created_at) {
              return new Date(b.created_at) - new Date(a.created_at);
            }
            return (b.id ?? 0) - (a.id ?? 0);
          });

          setLatestNotes(sorted.slice(0, LATEST_COUNT));
        }
      })
      .catch((err) => console.error("Latest notes fetch error:", err))
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, [baseUrl]);

  // ── Same pattern as the other featured sections: clicking a card
  // swaps in the existing NoteDetailPage. ──
  if (selectedNote) {
    return (
      <NoteDetailPage
        note={selectedNote}
        onBack={() => setSelectedNote(null)}
      />
    );
  }

  if (!loading && latestNotes.length === 0) return null;

  return (
    <div className="gcStudent-featured-section">
      <h2 className="gcStudent-featured-title">Latest Notes</h2>
      <p className="gcStudent-featured-subtitle">
        Recently shared notes from your teachers
      </p>

      {loading ? (
        <Row
          className="g-4 gcStudent-featured-grid"
          style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexWrap: "wrap", justifyContent: "center" }}
        >
          {Array.from({ length: LATEST_COUNT }).map((_, i) => (
            <Col key={`skeleton-${i}`} xs={12} sm={6} lg={3}>
              <div className="gcStudent-note-skeleton-card">
                <div className="gcStudent-note-skeleton-topbar" />
                <div className="gcStudent-note-skeleton-body">
                  <div className="gcStudent-note-skeleton-line gcStudent-note-skeleton-label" />
                  <div className="gcStudent-note-skeleton-line gcStudent-note-skeleton-tag" />
                  <div className="gcStudent-note-skeleton-line gcStudent-note-skeleton-title" />
                  <div className="gcStudent-note-skeleton-line gcStudent-note-skeleton-title-short" />
                </div>
                <div className="gcStudent-note-skeleton-divider" />
                <div className="gcStudent-note-skeleton-footer">
                  <div className="gcStudent-note-skeleton-avatar" />
                  <div className="gcStudent-note-skeleton-meta">
                    <div className="gcStudent-note-skeleton-line gcStudent-note-skeleton-name" />
                    <div className="gcStudent-note-skeleton-line gcStudent-note-skeleton-date" />
                  </div>
                </div>
                <div className="gcStudent-note-skeleton-btn" />
              </div>
            </Col>
          ))}
        </Row>
      ) : (
        <Row
          className="g-4 gcStudent-featured-grid"
          style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexWrap: "wrap", justifyContent: "center" }}
        >
          {latestNotes.map((note, i) => (
            <Col key={note.id} xs={12} sm={6} lg={3}>
              <NoteCard note={note} index={i} onClick={setSelectedNote} />
            </Col>
          ))}
        </Row>
      )}

      <div className="gcStudent-featured-viewall-row">
        <button className="gcStudent-featured-viewall-btn" onClick={onViewAll}>
          View All Notes
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M5 12h14M13 5l7 7-7 7"
              stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default FeaturedNotesSection;